import * as THREE from "three";
import { T, isGround, isSolid, type AsteroidBody, type Biome } from "@dyson/shared";

/**
 * The small life round an asteroid's edge, all made on the client from the
 * rock's own tiles and seed (nothing is sent), as the trees are:
 * - a fringe along its surface: grass, curling ferns, glowing bulbs, odd
 *   flowers, fronds, mushrooms, crystals and pebbles, set behind the ground so
 *   only their tops show, with a few low tufts in front of whoever walks by;
 * - things hanging from overhangs and cave roofs: roots, leafy vines, moss,
 *   glowing pods;
 * - a darker layer of rock behind it, a little bigger, which shifts as the
 *   camera moves, so the rock has some thickness;
 * - a faint haze round it, and motes drifting up off its surface (pollen,
 *   spores, snow, embers, fireflies), by biome.
 * What grows (and hangs) is gone once the ground it grows from is dug.
 */

/** Seconds, for the sway of the fringe and the drift of the motes (see tickLife). */
const lifeTime = { value: 0 };

/** Call once a frame with the renderer's clock. */
export function tickLife(time: number): void {
  lifeTime.value = time;
}

// ------------------------------------------------------------ what grows where

/** The sprites in the atlas: growing up out of the ground, or hanging down from it. */
const UP = ["grass", "fern", "bulb", "flower", "pebbles", "crystal", "frond", "mushroom"] as const;
const HANG = ["roots", "vine", "moss", "pods"] as const;
type Up = (typeof UP)[number];
type Hang = (typeof HANG)[number];

interface Palette {
  /** Leaves and stalks: dark, mid, light. */
  leaf: [string, string, string];
  /** Woody stalks and roots. */
  stalk: string;
  /** Flowers and caps: mid, light. */
  accent: [string, string];
  /** What glows: bulbs, pods, flower hearts. */
  glow: string;
  /** Stones and crystals: dark, mid, light. */
  stone: [string, string, string];
  /** The rock behind the rock (see the backdrop). */
  back: string;
  /** The haze round it, and its motes (null: none). */
  haze: string | null;
  mote: string | null;
  /** How likely each kind is on an open top edge (they needn't add to 1), and each hanging kind under an overhang. */
  up: Partial<Record<Up, number>>;
  hang: Partial<Record<Hang, number>>;
  /** Share of open edges with something growing (up) or hanging. */
  density: number;
  hangDensity: number;
}

const GREEN: Pick<Palette, "leaf" | "stalk"> = { leaf: ["#1f4a1c", "#3f8a33", "#7cc456"], stalk: "#5a3d22" };
const STONE: [string, string, string] = ["#3a3632", "#5f5852", "#8a827a"];

const PALETTES: Record<Biome, Palette> = {
  meadow: { ...GREEN, accent: ["#c4508a", "#f08cc0"], glow: "#ffe58a", stone: STONE, back: "#1c2418", haze: "#9fe07a", mote: "#fff2a8", up: { grass: 5, fern: 1.5, flower: 1.5, bulb: 0.7, frond: 0.8, pebbles: 0.6, mushroom: 0.4 }, hang: { roots: 2, vine: 2, moss: 1 }, density: 0.6, hangDensity: 0.45 },
  grove: { leaf: ["#173a24", "#2f6e44", "#5fae6a"], stalk: "#4a3420", accent: ["#e0e070", "#fffbb0"], glow: "#d8ff9a", stone: STONE, back: "#15211a", haze: "#7fd09a", mote: "#f4fff0", up: { grass: 3, fern: 3, frond: 2, flower: 0.6, mushroom: 0.8 }, hang: { vine: 3, roots: 1.5, moss: 2 }, density: 0.7, hangDensity: 0.6 },
  twilight: { leaf: ["#24183e", "#4a3478", "#8a6cc8"], stalk: "#3a2a4a", accent: ["#3ac0c8", "#9af4f0"], glow: "#8ff0ff", stone: ["#2a2638", "#46405a", "#6a6286"], back: "#16121f", haze: "#8a6cff", mote: "#a8f4ff", up: { bulb: 3, mushroom: 2.5, fern: 1.5, frond: 1, grass: 1, crystal: 0.6 }, hang: { pods: 3, vine: 1.5, moss: 1 }, density: 0.55, hangDensity: 0.55 },
  lichen: { leaf: ["#3a3a1c", "#6b6a34", "#a7a45c"], stalk: "#4a3e28", accent: ["#d68a4a", "#f0c080"], glow: "#f0e0a0", stone: STONE, back: "#1e1d15", haze: null, mote: "#e8e0b0", up: { grass: 2, pebbles: 2, mushroom: 1, fern: 0.8 }, hang: { roots: 2, moss: 2 }, density: 0.4, hangDensity: 0.35 },
  frost: { leaf: ["#3a5670", "#80a8c4", "#dcf0ff"], stalk: "#5d6d80", accent: ["#bfe6ff", "#ffffff"], glow: "#e8fbff", stone: ["#4f5a63", "#8296a8", "#c8e0f0"], back: "#1a2129", haze: "#bfe6ff", mote: "#ffffff", up: { crystal: 3, grass: 1.5, frond: 1, pebbles: 1, flower: 0.6 }, hang: { roots: 1, pods: 1 }, density: 0.45, hangDensity: 0.25 },
  dark: { leaf: ["#123030", "#1f5a58", "#40a8a0"], stalk: "#2a3434", accent: ["#2ad0c0", "#9affe8"], glow: "#7affe8", stone: ["#24242a", "#3e3e48", "#5e5e6c"], back: "#101014", haze: null, mote: "#7affe8", up: { mushroom: 2, pebbles: 2, crystal: 1.5, bulb: 1 }, hang: { pods: 2, roots: 1 }, density: 0.3, hangDensity: 0.3 },
  wreck: { ...GREEN, accent: ["#b0a080", "#e0d0b0"], glow: "#ffd27a", stone: ["#3a3530", "#6a5a48", "#9a8468"], back: "#1c1a16", haze: null, mote: "#ffe8b0", up: { grass: 3, pebbles: 2, fern: 1, mushroom: 1 }, hang: { roots: 2, vine: 1 }, density: 0.45, hangDensity: 0.35 },
  scorched: { leaf: ["#2a2624", "#4f4945", "#86807a"], stalk: "#2f2b29", accent: ["#ff7a2a", "#ffc070"], glow: "#ffb050", stone: ["#2a2420", "#4a3e36", "#6e6058"], back: "#1a1210", haze: "#ff7a2a", mote: "#ffa040", up: { pebbles: 3, crystal: 1.5, grass: 0.6 }, hang: { roots: 1 }, density: 0.3, hangDensity: 0.15 },
  barren: { ...GREEN, accent: ["#888070", "#b0a898"], glow: "#d0c8b0", stone: STONE, back: "#18171a", haze: null, mote: null, up: { pebbles: 3, crystal: 0.5 }, hang: { roots: 1 }, density: 0.25, hangDensity: 0.1 },
  living: { leaf: ["#1d4419", "#3f8430", "#9fd15a"], stalk: "#5c4029", accent: ["#e0a040", "#ffd890"], glow: "#ffe08a", stone: ["#33241a", "#5c4029", "#7d5838"], back: "#1a120c", haze: "#ffcf6a", mote: "#ffe890", up: { fern: 2, mushroom: 2, grass: 2, bulb: 1, frond: 1 }, hang: { moss: 2, vine: 2, roots: 1, pods: 1 }, density: 0.6, hangDensity: 0.55 },
  water: { ...GREEN, accent: ["#5ab0e0", "#a0e0ff"], glow: "#a0e0ff", stone: STONE, back: "#101820", haze: null, mote: null, up: {}, hang: {}, density: 0, hangDensity: 0 },
};

// ------------------------------------------------------------ the atlas

/** Art pixels per block (the tiles' and trees' own). */
const PX = 32;
/** Each sprite's cell: two blocks wide, four tall; one that grows has its root at the bottom middle, one that hangs at the top middle. */
const CW = 64;
const CH = 128;
const COLS = 4;
const CELLS = [...UP, ...HANG];
const ROWS = Math.ceil(CELLS.length / COLS);

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

/** A small, fast, seeded random (mulberry32). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One sprite's pixels, painted with flat colours, then outlined (as the trees and vines are). */
class Cell {
  readonly data = new Uint8ClampedArray(CW * CH * 4);
  put(x: number, y: number, c: RGB): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= CW || y >= CH) return;
    const i = (y * CW + x) * 4;
    this.data[i] = c[0];
    this.data[i + 1] = c[1];
    this.data[i + 2] = c[2];
    this.data[i + 3] = 255;
  }
  disc(cx: number, cy: number, r: number, c: RGB, ry = r): void {
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
      for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) if (((x + 0.5 - cx) / r) ** 2 + ((y + 0.5 - cy) / ry) ** 2 <= 1) this.put(x, y, c);
    }
  }
  /** A stroke along `at` (t from 0 to 1), `width` across (half-width), lit along its left: its body, then a highlight. */
  stroke(at: (t: number) => [number, number], width: (t: number) => number, body: RGB, light: RGB | null, steps = 80): void {
    const pts: [number, number, number][] = [];
    for (let k = 0; k <= steps; k++) {
      const [x, y] = at(k / steps);
      pts.push([x, y, width(k / steps)]);
    }
    for (const [x, y, w] of pts) this.disc(x, y, Math.max(0.6, w), body);
    if (light) for (const [x, y, w] of pts) if (w > 1.2) this.disc(x - w * 0.35, y - w * 0.15, w * 0.45, light);
  }
  /** A dark outline round everything painted. */
  outline(c: RGB): void {
    const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < CW && y < CH && this.data[(y * CW + x) * 4 + 3]! > 0;
    const edge: number[] = [];
    for (let y = 0; y < CH; y++) for (let x = 0; x < CW; x++) if (!solid(x, y) && (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1))) edge.push(x, y);
    for (let k = 0; k < edge.length; k += 2) this.put(edge[k]!, edge[k + 1]!, c);
  }
}

/** Paint one sprite of a palette into a cell: up ones grow from (32, 127) upward, hanging ones from (32, 0) down. */
function paint(kind: Up | Hang, pal: Palette, seed: number): Cell {
  const c = new Cell();
  const r = rng(seed);
  const range = (a: number, b: number) => a + (b - a) * r();
  const [dark, mid, light] = pal.leaf.map(hex) as [RGB, RGB, RGB];
  const stalk = hex(pal.stalk);
  const [acc, accLight] = pal.accent.map(hex) as [RGB, RGB];
  const glow = hex(pal.glow);
  const white: RGB = [255, 250, 230];
  const [sDark, sMid, sLight] = pal.stone.map(hex) as [RGB, RGB, RGB];
  const B = CH - 1;
  /** A curving stalk from (x0, base) up `h`, leaning `bend` by its top; returns where it is at t. */
  const stem = (x0: number, h: number, bend: number) => (t: number): [number, number] => [x0 + bend * t * t, B - h * t];
  const hangStem = (x0: number, len: number, wave: number, ph: number) => (t: number): [number, number] => [x0 + Math.sin(t * 7 + ph) * wave * t, len * t];
  switch (kind) {
    case "grass": {
      const n = Math.round(range(5, 8));
      for (let k = 0; k < n; k++) {
        const f = stem(32 + range(-14, 14), range(48, 100), range(-20, 20));
        c.stroke(f, (t) => range(2.2, 3.2) * (1 - t * 0.85), k % 2 ? mid : light, k % 2 ? light : null, 60);
      }
      break;
    }
    case "fern": {
      // A stalk up, coiling into a spiral at its tip, with leaflets along it.
      const h = range(60, 90);
      const bend = range(-10, 10);
      const f = stem(32, h, bend);
      c.stroke(f, (t) => 2.6 - t * 1.2, mid, light);
      for (let t = 0.2; t < 0.95; t += 0.12) {
        const [x, y] = f(t);
        const s = (t * 10) % 2 < 1 ? -1 : 1;
        c.disc(x + s * 5, y - 2, 3.5, s < 0 ? light : mid, 2.2);
      }
      const [tx, ty] = f(1);
      const dir = bend >= 0 ? 1 : -1;
      for (let a = 0; a < Math.PI * 3.2; a += 0.08) {
        const rad = 13 * (1 - a / (Math.PI * 3.4));
        c.disc(tx + dir * (rad * Math.cos(a) - 13), ty - rad * Math.sin(a) - 2, Math.max(0.8, 2.6 - a * 0.2), a < 4 ? mid : light);
      }
      break;
    }
    case "bulb": {
      const n = r() < 0.5 ? 1 : 2;
      for (let k = 0; k < n; k++) {
        const f = stem(32 + (n > 1 ? (k ? 9 : -9) : 0), range(55, 100) * (k ? 0.75 : 1), range(-12, 12));
        c.stroke(f, () => 1.7, stalk, null, 60);
        const [x, y] = f(1);
        const rad = range(5.5, 7.5);
        c.disc(x, y - rad + 1, rad, glow);
        c.disc(x - rad * 0.35, y - rad * 1.3 + 1, rad * 0.35, white);
      }
      break;
    }
    case "flower": {
      const f = stem(32, range(45, 75), range(-12, 12));
      c.stroke(f, () => 1.8, mid, null, 60);
      const [x, y] = f(1);
      const n = 5;
      const tilt = range(0, 1);
      for (let k = 0; k < n; k++) {
        const a = tilt + (k / n) * Math.PI * 2;
        c.disc(x + Math.cos(a) * 7, y - 6 + Math.sin(a) * 7, 5, k % 2 ? acc : accLight, 4);
      }
      c.disc(x, y - 6, 3.5, glow);
      break;
    }
    case "pebbles": {
      const n = Math.round(range(2, 4));
      for (let k = 0; k < n; k++) {
        const rx = range(5, 10);
        const ry = rx * range(0.55, 0.8);
        const x = 32 + range(-16, 16);
        c.disc(x, B - ry + 1, rx, sMid, ry);
        c.disc(x - rx * 0.3, B - ry * 1.3 + 1, rx * 0.45, sLight, ry * 0.4);
      }
      break;
    }
    case "crystal": {
      const n = Math.round(range(2, 4));
      for (let k = 0; k < n; k++) {
        const x0 = 32 + range(-12, 12);
        const h = range(22, 62) * (k === 0 ? 1 : 0.7);
        const w = range(4, 7);
        const tilt = range(-0.35, 0.35);
        for (let y = 0; y < h; y++) {
          const t = y / h;
          const half = w * (1 - t);
          const cx = x0 + tilt * y;
          for (let x = Math.floor(cx - half); x <= Math.ceil(cx + half); x++) c.put(x, B - y, x < cx ? sLight : x < cx + half * 0.5 ? sMid : sDark);
        }
      }
      break;
    }
    case "frond": {
      const h = range(85, 120);
      const bend = range(-18, 18);
      const f = stem(32, h, bend);
      c.stroke(f, (t) => 2.4 - t * 1.4, mid, light);
      for (let t = 0.25; t < 0.97; t += 0.07) {
        const [x, y] = f(t);
        const len = 14 * (1 - t) + 4;
        for (const s of [-1, 1]) c.stroke((u) => [x + s * len * u, y - len * 0.6 * u - 4 * u * u], (u) => 1.6 * (1 - u * 0.6), s < 0 ? light : mid, null, 20);
      }
      break;
    }
    case "mushroom": {
      const n = Math.round(range(2, 3));
      for (let k = 0; k < n; k++) {
        const x = 32 + range(-14, 14);
        const h = range(16, 40) * (k === 0 ? 1 : 0.7);
        const f = stem(x, h, range(-4, 4));
        c.stroke(f, () => 2.4, accLight, null, 30);
        const [tx, ty] = f(1);
        const rx = range(8, 13) * (k === 0 ? 1 : 0.75);
        const ry = rx * 0.6;
        for (let y = Math.floor(ty - ry); y <= ty; y++) for (let xx = Math.floor(tx - rx); xx <= Math.ceil(tx + rx); xx++) if (((xx + 0.5 - tx) / rx) ** 2 + ((y + 0.5 - ty) / ry) ** 2 <= 1) c.put(xx, y, acc);
        for (let s = 0; s < 3; s++) c.disc(tx + range(-rx * 0.6, rx * 0.6), ty - range(1, ry * 0.7), 1.4, glow);
      }
      break;
    }
    case "roots": {
      const n = Math.round(range(3, 5));
      for (let k = 0; k < n; k++) c.stroke(hangStem(32 + range(-14, 14), range(40, 110), range(3, 6), range(0, 6)), (t) => 2.4 * (1 - t * 0.7), stalk, null, 70);
      break;
    }
    case "vine": {
      const n = r() < 0.5 ? 1 : 2;
      for (let k = 0; k < n; k++) {
        const f = hangStem(32 + range(-10, 10), range(70, 124), range(3, 6), range(0, 6));
        c.stroke(f, () => 1.6, dark, null, 80);
        for (let t = 0.08; t < 1; t += 0.09) {
          const [x, y] = f(t);
          const s = Math.round(t / 0.09) % 2 ? 1 : -1;
          c.disc(x + s * 4, y + 1, 3.4, s < 0 ? light : mid, 2.2);
        }
      }
      break;
    }
    case "moss": {
      for (let x = 10; x < 54; x += 2) c.disc(x, range(1, 5), range(3, 6), (x / 2) % 2 ? mid : dark);
      const n = Math.round(range(5, 8));
      for (let k = 0; k < n; k++) {
        const f = hangStem(range(12, 52), range(12, 40), 1, range(0, 6));
        c.stroke(f, () => 1.3, mid, null, 30);
        const [x, y] = f(1);
        c.disc(x, y, 2, light);
      }
      break;
    }
    case "pods": {
      const n = Math.round(range(2, 4));
      for (let k = 0; k < n; k++) {
        const f = hangStem(32 + range(-14, 14), range(28, 90), range(1, 3), range(0, 6));
        c.stroke(f, () => 1.2, dark, null, 60);
        const [x, y] = f(1);
        c.disc(x, y + 3, range(3.5, 5), glow, range(4.5, 6));
        c.disc(x - 1.2, y + 1.5, 1.3, white);
      }
      break;
    }
  }
  c.outline(hex(pal.leaf[0] === "#123030" ? "#06100f" : "#0b0d08"));
  return c;
}

const atlases = new Map<Biome, THREE.Texture>();

/** A biome's sprites, painted once and kept. */
function atlasFor(biome: Biome): THREE.Texture {
  let tex = atlases.get(biome);
  if (tex) return tex;
  const pal = PALETTES[biome];
  const canvas = document.createElement("canvas");
  canvas.width = COLS * CW;
  canvas.height = ROWS * CH;
  const ctx = canvas.getContext("2d")!;
  CELLS.forEach((kind, k) => {
    const cell = paint(kind, pal, 1013 * (k + 1) + biome.length * 7);
    ctx.putImageData(new ImageData(cell.data, CW, CH), (k % COLS) * CW, Math.floor(k / COLS) * CH);
  });
  tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  atlases.set(biome, tex);
  return tex;
}

// ------------------------------------------------------------ the sprites

/** In front of the fringe behind (and the trees), but behind the ground: only the tops show over it. */
const BACK_Z = -0.45;
/** Low tufts in front of the characters. */
const FRONT_Z = 0.3;

interface Sprite {
  /** The ground tile it grows from, and the open one it grows into: gone once either changes. */
  ground: number;
  open: number;
  kind: Up | Hang;
  x: number;
  y: number;
  /** Which way it grows (a unit vector). */
  dx: number;
  dy: number;
  size: number;
  flip: boolean;
  z: number;
  phase: number;
}

/** A tile's own random number, for kind k of choice (0..1). */
function hash(x: number, y: number, k: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(k + 1, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function pick<K extends string>(weights: Partial<Record<K, number>>, roll: number): K | null {
  const entries = Object.entries(weights) as [K, number][];
  const total = entries.reduce((n, [, w]) => n + w, 0);
  if (!total) return null;
  let at = roll * total;
  for (const [k, w] of entries) {
    if (at < w) return k;
    at -= w;
  }
  return entries[entries.length - 1]![0];
}

const SIDES = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

export class AsteroidLife {
  readonly group = new THREE.Group();
  private readonly sprites: Sprite[] = [];
  private readonly byTile = new Map<number, number[]>();
  private readonly gone = new Set<number>();
  private fringe?: THREE.Mesh;
  /** The haze round it: sunlit air, so it goes with the sun (see setSun). */
  private haze?: THREE.MeshBasicMaterial;
  private readonly owned: { dispose(): void }[] = [];
  private readonly pal: Palette;
  private readonly biome: Biome;

  constructor(
    private readonly a: AsteroidBody,
    private readonly light: (x: number, y: number) => number,
    reached: Uint8Array,
  ) {
    const def = a.def;
    this.biome = def.kind === "water" ? "water" : def.dead ? "barren" : (def.biome ?? (def.kind === "home" ? "meadow" : "dark"));
    this.pal = PALETTES[this.biome];
    if (this.biome === "water") return;
    const g = a.grid;
    const cx = g.w / 2;
    const cy = g.h / 2;
    let ground = 0;
    for (let y = 1; y < g.h - 1; y++) {
      for (let x = 1; x < g.w - 1; x++) {
        const i = y * g.w + x;
        if (!isGround(g.tiles[i]!)) continue;
        ground++;
        const rx = x + 0.5 - cx;
        const ry = y + 0.5 - cy;
        const rl = Math.hypot(rx, ry) || 1;
        SIDES.forEach(([sx, sy], k) => {
          const j = (y + sy) * g.w + x + sx;
          if (g.tiles[j] !== T.EMPTY) return;
          // Which way this open side faces, against out from the middle: up out of the ground, or down from an overhang or cave roof.
          const d = (sx * rx + sy * ry) / rl;
          const roll = hash(x, y, k);
          const kindRoll = hash(x, y, k + 11);
          const outside = !!reached[j];
          if (d > 0.35 && roll < this.pal.density) {
            const kind = pick(this.pal.up, kindRoll);
            if (!kind) return;
            // Grows out between the side's way and straight out from the middle.
            let gx = sx + rx / rl;
            let gy = sy + ry / rl;
            const gl = Math.hypot(gx, gy) || 1;
            gx /= gl;
            gy /= gl;
            const size = (kind === "pebbles" ? 0.55 : 0.7) + hash(x, y, k + 23) * 0.5;
            this.add({ ground: i, open: j, kind, x: x + 0.5 + sx * 0.5 - gx * 0.35, y: y + 0.5 + sy * 0.5 - gy * 0.35, dx: gx, dy: gy, size, flip: hash(x, y, k + 31) < 0.5, z: BACK_Z, phase: roll * 40 });
            // Now and then a low tuft in front, out on the open surface.
            if (outside && kind !== "pebbles" && hash(x, y, k + 41) < 0.3) {
              this.add({ ground: i, open: j, kind: "grass", x: x + 0.5 + sx * 0.5 - gx * 0.2, y: y + 0.5 + sy * 0.5 - gy * 0.2, dx: gx, dy: gy, size: 0.32 + hash(x, y, k + 43) * 0.15, flip: hash(x, y, k + 47) < 0.5, z: FRONT_Z, phase: roll * 33 });
            }
          } else if (d < -0.35 && roll < this.pal.hangDensity) {
            const kind = pick(this.pal.hang, kindRoll);
            if (!kind) return;
            this.add({ ground: i, open: j, kind, x: x + 0.5 + sx * 0.5 - sx * 0.25, y: y + 0.5 + sy * 0.5 - sy * 0.25, dx: sx, dy: sy, size: 0.6 + hash(x, y, k + 53) * 0.5, flip: hash(x, y, k + 59) < 0.5, z: BACK_Z, phase: roll * 27 });
          }
        });
      }
    }
    // A structure begun in open space isn't a rock: nothing grows on it.
    if (!ground) return;
    this.buildFringe();
    const profile = this.profile();
    this.buildBackdrop(profile);
    if (this.pal.haze) this.buildHaze(profile);
    if (this.pal.mote) this.buildMotes(profile);
  }

  private add(s: Sprite): void {
    const n = this.sprites.push(s) - 1;
    for (const t of [s.ground, s.open]) this.byTile.set(t, [...(this.byTile.get(t) ?? []), n]);
  }

  /** Tiles changed: whatever grew from dug ground (or into filled space) is gone. */
  update(indices: number | readonly number[]): void {
    let changed = false;
    for (const i of typeof indices === "number" ? [indices] : indices) {
      for (const n of this.byTile.get(i) ?? []) {
        if (this.gone.has(n)) continue;
        const s = this.sprites[n]!;
        const g = this.a.grid.tiles;
        if (isGround(g[s.ground]!) && !isSolid(g[s.open]!) && g[s.open] === T.EMPTY) continue;
        this.gone.add(n);
        changed = true;
      }
    }
    if (changed) this.buildFringe();
  }

  private buildFringe(): void {
    if (this.fringe) {
      this.group.remove(this.fringe);
      this.fringe.geometry.dispose();
    }
    const pos: number[] = [];
    const uv: number[] = [];
    const col: number[] = [];
    const bend: number[] = [];
    const phase: number[] = [];
    const index: number[] = [];
    this.sprites.forEach((s, n) => {
      if (this.gone.has(n)) return;
      const k = CELLS.indexOf(s.kind);
      const hanging = (HANG as readonly string[]).includes(s.kind);
      const w = (CW / PX) * s.size;
      const h = (CH / PX) * s.size;
      // Across: to its right as it grows.
      const ax = s.dy;
      const ay = -s.dx;
      const tipX = s.x + s.dx * h;
      const tipY = s.y + s.dy * h;
      const u0 = ((k % COLS) * CW) / (COLS * CW);
      const u1 = ((k % COLS) * CW + CW) / (COLS * CW);
      const vTop = 1 - (Math.floor(k / COLS) * CH) / (ROWS * CH);
      const vBottom = 1 - (Math.floor(k / COLS) * CH + CH) / (ROWS * CH);
      const [vBase, vTip] = hanging ? [vTop, vBottom] : [vBottom, vTop];
      const [uL, uR] = s.flip ? [u1, u0] : [u0, u1];
      const base = pos.length / 3;
      // Base left, base right, tip right, tip left (left: against "across").
      pos.push(s.x - ax * w / 2, s.y - ay * w / 2, s.z, s.x + ax * w / 2, s.y + ay * w / 2, s.z, tipX + ax * w / 2, tipY + ay * w / 2, s.z, tipX - ax * w / 2, tipY - ay * w / 2, s.z);
      uv.push(uL, vBase, uR, vBase, uR, vTip, uL, vTip);
      const lit = Math.min(1, Math.max(0.25, this.light(s.x, s.y)));
      for (let v = 0; v < 4; v++) col.push(lit, lit, lit);
      // The tip sways side to side, a hanging one more.
      const sway = h * (hanging ? 0.1 : 0.06);
      bend.push(0, 0, 0, 0, ax * sway, ay * sway, ax * sway, ay * sway);
      for (let v = 0; v < 4; v++) phase.push(s.phase);
      index.push(base, base + 1, base + 2, base, base + 2, base + 3);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
    g.setAttribute("aBend", new THREE.Float32BufferAttribute(bend, 2));
    g.setAttribute("aPhase", new THREE.Float32BufferAttribute(phase, 1));
    g.setIndex(index);
    if (!this.fringe) {
      const material = new THREE.MeshBasicMaterial({ map: atlasFor(this.biome), alphaTest: 0.5, vertexColors: true, side: THREE.DoubleSide });
      material.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = lifeTime;
        shader.vertexShader = shader.vertexShader
          .replace("#include <common>", "#include <common>\nuniform float uTime;\nattribute vec2 aBend;\nattribute float aPhase;")
          .replace("#include <begin_vertex>", "vec3 transformed = vec3(position);\ntransformed.xy += aBend * sin(uTime * 1.3 + aPhase);");
      };
      material.customProgramCacheKey = () => "asteroid-fringe";
      this.owned.push(material);
      this.fringe = new THREE.Mesh(g, material);
      this.fringe.frustumCulled = false;
    } else this.fringe.geometry = g;
    this.group.add(this.fringe);
  }

  /** How far the rock reaches from its middle, every half degree round (blocks). */
  private profile(): Float32Array {
    const g = this.a.grid;
    const cx = g.w / 2;
    const cy = g.h / 2;
    const n = 720;
    const out = new Float32Array(n);
    const max = Math.min(g.w, g.h) / 2;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      const c = Math.cos(a);
      const s = Math.sin(a);
      for (let r = max; r > 0; r -= 0.7) {
        const x = Math.floor(cx + c * r);
        const y = Math.floor(cy + s * r);
        if (x >= 0 && y >= 0 && x < g.w && y < g.h && isSolid(g.tiles[y * g.w + x]!)) {
          out[k] = r;
          break;
        }
      }
    }
    return out;
  }

  /**
   * A darker layer of rock behind the rock, a little bigger, its edge
   * rougher: it sits further back, so as the camera moves it shifts against
   * the rock in front, and the rock looks thick rather than cut out.
   */
  private buildBackdrop(profile: Float32Array): void {
    const g = this.a.grid;
    const cx = g.w / 2;
    const cy = g.h / 2;
    const n = profile.length;
    const seed = this.a.def.seed;
    const ph = [hash(seed, 1, 0), hash(seed, 2, 0), hash(seed, 3, 0)].map((v) => v * Math.PI * 2);
    const pos: number[] = [cx, cy, -6];
    for (let k = 0; k <= n; k++) {
      const a = ((k % n) / n) * Math.PI * 2;
      const r = profile[k % n]!;
      if (!r) {
        pos.push(cx, cy, -6);
        continue;
      }
      const rough = 1.5 * Math.sin(a * 7 + ph[0]!) + 1 * Math.sin(a * 13 + ph[1]!) + 0.6 * Math.sin(a * 29 + ph[2]!);
      // A few blocks past the edge, rising and falling like far hills.
      const R = r + 5 + rough * 1.6;
      pos.push(cx + Math.cos(a) * R, cy + Math.sin(a) * R, -6);
    }
    const index: number[] = [];
    for (let k = 1; k <= n; k++) index.push(0, k, k + 1);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geometry.setIndex(index);
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color(this.pal.back) });
    const mesh = new THREE.Mesh(geometry, material);
    this.owned.push(geometry, material);
    this.group.add(mesh);
  }

  /** A faint glow round the rock in its biome's colour, following its outline: brightest at the edge, fading out a short way past it. */
  private buildHaze(profile: Float32Array): void {
    const g = this.a.grid;
    const cx = g.w / 2;
    const cy = g.h / 2;
    const n = profile.length;
    const edge = Math.max(...profile);
    const width = Math.min(30, edge * 0.35 + 4);
    const tint = new THREE.Color(this.pal.haze!).multiplyScalar(0.16);
    const pos: number[] = [];
    const col: number[] = [];
    for (let k = 0; k <= n; k++) {
      const a = ((k % n) / n) * Math.PI * 2;
      const r = profile[k % n]! || edge * 0.5;
      // Smoothed a little round the outline, so it doesn't jag with every block.
      const near = (profile[(k + n - 3) % n]! + profile[(k + 3) % n]! + r) / 3 || r;
      const [c, sn] = [Math.cos(a), Math.sin(a)];
      pos.push(cx + c * (near - 2), cy + sn * (near - 2), -7, cx + c * (near + width), cy + sn * (near + width), -7);
      col.push(tint.r, tint.g, tint.b, 0, 0, 0);
    }
    const index: number[] = [];
    for (let k = 0; k < n; k++) {
      const i = k * 2;
      index.push(i, i + 1, i + 3, i, i + 3, i + 2);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
    geometry.setIndex(index);
    // Light added to what's behind it: black adds nothing.
    const material = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
    this.owned.push(geometry, material);
    this.haze = material;
    this.group.add(new THREE.Mesh(geometry, material));
  }

  /** Motes drifting up off the surface and fading, in its biome's colour: pollen, spores, snow, embers, fireflies. */
  private buildMotes(profile: Float32Array): void {
    const g = this.a.grid;
    const cx = g.w / 2;
    const cy = g.h / 2;
    const n = profile.length;
    const perimeter = profile.reduce((s, r) => s + r, 0) * ((Math.PI * 2) / n);
    const count = Math.round(Math.min(400, Math.max(30, perimeter / 4)));
    const r = rng(this.a.def.seed ^ 0x3077);
    const pos: number[] = [];
    const dir: number[] = [];
    const phase: number[] = [];
    const speed: number[] = [];
    for (let k = 0; k < count; k++) {
      const at = Math.floor(r() * n);
      const rad = profile[at]!;
      if (!rad) continue;
      const a = (at / n) * Math.PI * 2;
      const d = rad + 0.5 + r() * 9;
      pos.push(cx + Math.cos(a) * d, cy + Math.sin(a) * d, -0.2);
      dir.push(Math.cos(a), Math.sin(a));
      phase.push(r());
      speed.push(0.6 + r() * 0.8);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geometry.setAttribute("aDir", new THREE.Float32BufferAttribute(dir, 2));
    geometry.setAttribute("aPhase", new THREE.Float32BufferAttribute(phase, 1));
    geometry.setAttribute("aSpeed", new THREE.Float32BufferAttribute(speed, 1));
    const material = new THREE.PointsMaterial({ map: dotTexture(), color: new THREE.Color(this.pal.mote!), size: 0.9, sizeAttenuation: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = lifeTime;
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nuniform float uTime;\nattribute vec2 aDir;\nattribute float aPhase;\nattribute float aSpeed;\nfloat moteFade;")
        .replace(
          "#include <begin_vertex>",
          `float cycle = fract(uTime * 0.05 * aSpeed + aPhase);
          vec3 transformed = vec3(position);
          // Rising off the surface as it drifts, wandering a little.
          transformed.xy += aDir * cycle * 8.0 + vec2(sin(uTime * 0.8 * aSpeed + aPhase * 6.28), cos(uTime * 0.6 * aSpeed + aPhase * 9.0)) * 1.2;
          // In and out over its rise, and twinkling.
          moteFade = sin(cycle * 3.14159) * (0.65 + 0.35 * sin(uTime * 3.0 * aSpeed + aPhase * 20.0));`,
        )
        .replace("gl_PointSize = size;", "gl_PointSize = size * moteFade;");
    };
    material.customProgramCacheKey = () => "asteroid-motes";
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    this.owned.push(geometry, material);
    this.group.add(points);
  }

  /** How bright the suns are now, against usual (1): an outage dims the fringe of life with the ground (see NIGHT). */
  setSun(k: number): void {
    (this.fringe?.material as THREE.MeshBasicMaterial | undefined)?.color.setScalar(k);
    this.haze?.color.setScalar(Math.max(0, Math.min(1, (k - 0.3) / 0.7)));
  }

  dispose(): void {
    this.fringe?.geometry.dispose();
    for (const o of this.owned) o.dispose();
  }
}

let dot: THREE.Texture | undefined;
/** A soft round dot, for the motes. */
function dotTexture(): THREE.Texture {
  if (dot) return dot;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 32;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.35, "rgba(255,255,255,0.8)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 32, 32);
  dot = new THREE.CanvasTexture(canvas);
  return dot;
}
